import { useEffect, useState } from "react";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import { classNames } from "../utils";
import {
  isProfileAddConfirmationValid,
  OTHER_ENTITY_MAX_LENGTH,
  PROFILE_CONFIRM_ENTITIES,
  ProfileAddConfirmation,
  ProfileConfirmEntityId,
} from "./profileConfirmEntities";

type EntityUsedToConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (selection: ProfileAddConfirmation) => void;
  initialEntities?: ProfileConfirmEntityId[];
};

export const EntityUsedToConfirmModal = ({
  open,
  onClose,
  onSubmit,
  initialEntities = [],
}: EntityUsedToConfirmModalProps) => {
  const [selectedEntities, setSelectedEntities] = useState<
    ProfileConfirmEntityId[]
  >([]);
  const [otherText, setOtherText] = useState("");

  const resetSelection = () => {
    setSelectedEntities([]);
    setOtherText("");
  };

  useEffect(() => {
    if (open) {
      setSelectedEntities(initialEntities);
      setOtherText("");
    } else {
      resetSelection();
    }
    // Seed only when the modal opens/closes; ignore later initialEntities churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selection: ProfileAddConfirmation = {
    entities: selectedEntities,
    ...(selectedEntities.includes("other") ? { otherText } : {}),
  };

  const toggleEntity = (entityId: ProfileConfirmEntityId) => {
    setSelectedEntities((prev) => {
      const isSelected = prev.includes(entityId);
      if (isSelected) {
        const entities = prev.filter((id) => id !== entityId);
        if (!entities.includes("other")) {
          setOtherText("");
        }
        return entities;
      }
      return [...prev, entityId];
    });
  };

  const handleClose = () => {
    resetSelection();
    onClose();
  };

  const handleSubmit = () => {
    if (!isProfileAddConfirmationValid(selection)) {
      return;
    }

    const payload: ProfileAddConfirmation = {
      entities: selectedEntities,
    };

    if (selectedEntities.includes("other")) {
      payload.otherText = otherText.trim();
    }

    onSubmit(payload);
    resetSelection();
  };

  const showOtherInput = selectedEntities.includes("other");
  const canSubmit = isProfileAddConfirmationValid(selection);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Entity Used to Confirm"
      width="w-full max-w-2xl"
      titleSize="text-xl"
    >
      <div className="mt-4 rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-2 gap-3">
          {PROFILE_CONFIRM_ENTITIES.map(({ id, label, icon: Icon }) => {
            const isSelected = selectedEntities.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleEntity(id)}
                className={classNames(
                  "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors",
                  isSelected
                    ? "border-slate-800 bg-slate-800 text-white"
                    : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                )}
              >
                {Icon && (
                  <Icon
                    className={classNames(
                      "h-5 w-5 flex-shrink-0",
                      isSelected ? "text-white" : "text-gray-500"
                    )}
                    aria-hidden
                  />
                )}
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {showOtherInput && (
        <div className="mt-4">
          <label
            htmlFor="otherEntityReason"
            className="mb-2 block text-sm font-medium text-gray-600"
          >
            Specify what other entity was used
          </label>
          <textarea
            id="otherEntityReason"
            name="otherEntityReason"
            data-testid="other-entity-reason-input"
            value={otherText}
            maxLength={OTHER_ENTITY_MAX_LENGTH}
            rows={4}
            placeholder="Type here..."
            onChange={({ target }) =>
              setOtherText(target.value.slice(0, OTHER_ENTITY_MAX_LENGTH))
            }
            className="block w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          text="Submit"
          colorType={ButtonColorTypes.primary}
          disabled={!canSubmit}
          onClick={handleSubmit}
        />
      </div>
    </Modal>
  );
};

export default EntityUsedToConfirmModal;
